/*
 * Player doctrine for Survivor equipment.
 *
 * This is the semantic contract above the numeric utility model. Each scenario
 * states the real in-match success condition, the order in which a player makes
 * the decision, common reasoning traps, and a regression oracle. Stats may
 * change after a patch; these goals must not silently change with them.
 */
function doctrine (success, decisionOrder, failureModes, oracle, activation) {
  return {
    success,
    decisionOrder,
    failureModes,
    oracle,
    activation: activation || 'plan'
  }
}

export const EQUIPMENT_PLAYER_DOCTRINE = {
  team_aura_tracking: doctrine(
    'Keep the team-reading window long and wide enough to make a rotation decision.',
    ['information_reach', 'decision_window', 'repeat_uses'],
    ['Valuing charges before the aura covers the relevant rotation.', 'Selecting a Key from a generic Aura or Teamwork tag without a proximity payoff.'],
    { itemId: 'skeleton_key', addonIds: ['braided_bauble', 'friendship_charm'] },
    'explicit'
  ),
  killer_aura_tracking: doctrine(
    'Unlock the global Killer read, then preserve a usable decision window while accepting reciprocal Aura exposure.',
    ['mechanic_unlock', 'global_information', 'decision_window', 'repeat_uses'],
    ['Scoring a Key without Blood Amber as Killer tracking.', 'Calling reciprocal Blood Amber exposure Stealth.'],
    { itemId: 'skeleton_key', addonIds: ['blood_amber', 'braided_bauble'] },
    'explicit'
  ),
  chest_endgame_utility: doctrine(
    'Complete the exposed chest interaction quickly while retaining the endgame Key route.',
    ['mechanic_unlock', 'execution_window', 'repeat_uses', 'endgame_payoff'],
    ['Treating Hatch access alone as repeatable mid-game value.', 'Selecting a Key for generic Item economy or Endgame perks.'],
    { itemId: 'skeleton_key', addonIds: ['friendship_charm', 'shrill_whistle'] },
    'explicit'
  ),
  obsession_coordination: doctrine(
    'Create the permanent Obsession information link and preserve enough Key economy to use it.',
    ['mechanic_unlock', 'repeat_uses', 'information_reach'],
    ['Letting generic aura duration replace Unique Wedding Ring.', 'Treating any Obsession tag as a linked-player payoff.'],
    { itemId: 'skeleton_key', addonIds: ['friendship_charm', 'unique_wedding_ring'] },
    'explicit'
  ),

  flashlight_save: doctrine(
    'Land a pickup Blind from an imperfect side angle during a short, non-repeatable window.',
    ['beam_cone', 'aim_stability', 'minimum_attempts', 'comfort_range'],
    ['Using Blind duration to recover an angle the Killer denied.', 'Calling a narrow long-range beam a safer pickup save.'],
    { itemId: 'sport_flashlight', addonIds: ['rubber_grip', 'wide_lens'] },
    'explicit'
  ),
  chase_blind: doctrine(
    'Convert a pallet or animation-locked Blind into repeatable chase distance and reposition time.',
    ['beam_reach', 'repeat_attempts', 'blind_payoff', 'aim_comfort'],
    ['Treating Wide Lens plus Grip as the chase-power ceiling.', 'Selecting a Flashlight from the generic Chase tag alone.'],
    { itemId: 'utility_flashlight', addonIds: ['high_end_sapphire_lens', 'long_life_battery'] },
    'explicit'
  ),
  blind_duration_pressure: doctrine(
    'Maximise the successful Blind window when the build has a real post-Blind disappearance payoff.',
    ['blind_payoff', 'minimum_attempts', 'comfort_range'],
    ['Scoring brightness.', 'Using maximum duration without a perk or route that converts the extra time.'],
    { itemId: 'utility_flashlight', addonIds: ['intense_halogen', 'odd_bulb'] },
    'explicit'
  ),
  flashlight_endurance: doctrine(
    'Maximise total usable beam time across repeated opportunities or recharge cycles.',
    ['battery_economy', 'comfort_range'],
    ['Letting aim stability beat a strictly longer battery in an endurance scenario.'],
    { itemId: 'utility_flashlight', addonIds: ['long_life_battery', 'low_amp_filament'] },
    'explicit'
  ),

  route_scouting: doctrine(
    'Read the largest personal route for long enough to choose the next safe objective.',
    ['personal_scan_radius', 'decision_window', 'repeat_uses'],
    ['Applying Beam penalties to the personal route scan.'],
    { itemId: 'annotated_map', addonIds: ['battered_tape', 'glowing_ink'] }
  ),
  totem_hunting: doctrine(
    'Unlock Totem tracking, then maximise the personal search radius.',
    ['mechanic_unlock', 'personal_scan_radius', 'repeat_uses', 'decision_window'],
    ['Ranking a larger scan that cannot track Totems.'],
    { itemId: 'annotated_map', addonIds: ['battered_tape', 'sharpened_flint'] }
  ),
  map_killer_scouting: doctrine(
    'Place a Killer-detection Beam and retain enough charges to make the information route repeatable.',
    ['mechanic_unlock', 'repeat_uses', 'beam_window'],
    ['Treating Battered Tape as Beam radius.', 'Ignoring Crimson Stamp charge and duration costs.'],
    { itemId: 'annotated_map', addonIds: ['crimson_stamp', 'gnarled_compass'] }
  ),
  team_objective_mapping: doctrine(
    'Place repeated team-readable objective Beams; personal scan range is only a secondary benefit.',
    ['repeat_uses', 'beam_window', 'personal_information'],
    ['Using personal route radius as the fixed team Beam radius.'],
    { itemId: 'annotated_map', addonIds: ['gnarled_compass'] }
  ),

  altruistic_heal_burst: doctrine(
    'Finish one exposed team reset in the shortest possible time.',
    ['heal_execution_time', 'one_complete_heal', 'leftover_economy'],
    ['Letting a second theoretical heal beat the fastest current reset.'],
    { itemId: 'ranger_med_kit', addonIds: ['abdominal_dressing', 'medical_scissors'] }
  ),
  altruistic_heal_economy: doctrine(
    'Produce several complete team resets without giving up the useful Ranger speed baseline.',
    ['complete_team_heals', 'charges', 'heal_execution_time'],
    ['Optimising a partial leftover that never completes another heal.'],
    { itemId: 'ranger_med_kit', addonIds: ['gel_dressings', 'self_adherent_wrap'] }
  ),
  self_heal_safety: doctrine(
    'Secure one complete personal reset as quickly as possible before interruption.',
    ['one_complete_self_heal', 'heal_execution_time', 'charge_efficiency'],
    ['Rewarding unused second-heal capacity in a one-reset emergency scenario.', 'Assuming rarer Med-Kits heal the user faster.'],
    { addonIds: ['abdominal_dressing', 'medical_scissors'] }
  ),
  double_self_heal: doctrine(
    'Cross the two-complete-self-heal charge breakpoint first; speed only breaks valid ties.',
    ['two_complete_self_heals', 'heal_execution_time', 'charge_efficiency'],
    ['Ranking a faster package that cannot complete the second reset.'],
    { addonIds: ['gel_dressings', 'self_adherent_wrap'] }
  ),
  healing_skill_checks: doctrine(
    'Create the most Healing Skill Checks and maximise the reward for consistently hitting Greats.',
    ['skill_check_frequency', 'great_check_payoff', 'great_zone_comfort', 'charges'],
    ['Letting generic Med-Kit charges replace the Needle and Suture conversion engine.'],
    { addonIds: ['needle_thread', 'surgical_suture'] },
    'explicit'
  ),
  exhaustion_reset: doctrine(
    'Consume the Med-Kit to recover Exhaustion only when the deck can activate a second chase payoff.',
    ['mechanic_unlock', 'heal_execution_time', 'remaining_economy'],
    ['Recommending the Syringe to a build with no Exhaustion perk.'],
    { addonIds: ['abdominal_dressing', 'anti_exhaustion_syringe'] },
    'explicit'
  ),

  generator_capacity: doctrine(
    'Maximise transferable Generator charges and every recharge or efficiency multiplier applied to them.',
    ['objective_progress', 'complete_capacity', 'delivery_safety'],
    ['Calling faster delivery extra Generator progress.'],
    { itemId: 'commodious_toolbox', addonIds: ['scraps', 'wire_spool'] }
  ),
  generator_burst: doctrine(
    'Maximise total immediate objective contribution before the Toolbox is depleted.',
    ['objective_contribution', 'transferable_progress', 'delivery_safety'],
    ['Optimising ten-second speed while losing more total Generator progress.'],
    { itemId: 'commodious_toolbox', addonIds: ['brand_new_part', 'wire_spool'] }
  ),
  brand_new_part_burst: doctrine(
    'Front-load the permanent requirement reduction and retain the largest useful follow-up capacity.',
    ['mechanic_unlock', 'objective_contribution', 'remaining_capacity'],
    ['Multiplying the consumed Brand New Part through a recharge perk.'],
    { itemId: 'commodious_toolbox', addonIds: ['brand_new_part', 'wire_spool'] }
  ),
  stealth_repair: doctrine(
    'Unlock quieter repairs while keeping enough capacity for the stealth window to matter.',
    ['mechanic_unlock', 'objective_progress', 'delivery_safety'],
    ['Ranking a fast loud Toolbox as stealth repair.'],
    { itemId: 'commodious_toolbox', addonIds: ['spring_clamp', 'wire_spool'] }
  ),
  sabotage_speed: doctrine(
    'Break the target Hook inside the carry window at the raw sabotage-speed ceiling.',
    ['sabotage_execution_time', 'minimum_complete_actions'],
    ['Renaming a balanced capacity package as the maximum-speed package.'],
    { itemId: 'alex_s_toolbox', addonIds: ['cutting_wire', 'hacksaw'] },
    'explicit'
  ),
  sabotage_denial: doctrine(
    'Cross the 1.5-second face-to-face sabotage threshold, then maximise five complete sabotages and twenty extra seconds of Hook downtime.',
    ['sabotage_execution_floor', 'five_complete_actions', 'denial_duration', 'notification_comfort'],
    ['Spending the capacity slot on another 0.2 seconds after Alex\'s Toolbox already clears the default hit-recovery window.', 'Using Protective Gloves for a sabotage performed in the Killer\'s face.'],
    { itemId: 'alex_s_toolbox', addonIds: ['grip_wrench', 'wire_spool'] },
    'explicit'
  ),

  dense_chase_concealment: doctrine(
    'Treat maximum internal opacity as an explicit high-variance experiment, never as a reliable close-range escape.',
    ['internal_opacity', 'minimum_coverage', 'deployment_time', 'minimum_duration'],
    ['Treating Potent Extract as a strict upgrade while its inside-cloud visibility is inconsistent across maps and lighting.', 'Pairing Potent Extract with more deployment speed while leaving only a six-metre cloud.'],
    { itemId: 'vigo_s_fog_vial', addonIds: ['mushroom_formula', 'potent_extract'] },
    'explicit'
  ),
  large_lasting_cloud: doctrine(
    'Pre-deploy an outside-in visual wall, then use its largest and longest boundary to hide the rotation after line of sight is broken.',
    ['coverage_radius', 'duration', 'opacity', 'repeat_uses'],
    ['Activating only after the Killer already shares the cloud.', 'Trading half the duration and a quarter of the area for an unreliable inside-cloud opacity gain.'],
    { itemId: 'vigo_s_fog_vial', addonIds: ['mushroom_formula', 'oily_sap'] }
  ),
  fast_deploy_cloud: doctrine(
    'Raise a wide outside-in screen quickly while the approaching Killer is still outside the cloud.',
    ['deployment_time', 'coverage_radius', 'minimum_duration', 'internal_opacity_comfort'],
    ['Treating the cloud as an emergency chase reset once the Killer is already inside.', 'Equating fast deployment with a tiny dense cloud.'],
    { itemId: 'vigo_s_fog_vial', addonIds: ['mushroom_formula', 'reactive_compound'] }
  ),
  repeatable_cloud: doctrine(
    'Reduce downtime while preserving a cloud worth deploying on every cycle.',
    ['recharge_time', 'coverage_radius', 'minimum_duration', 'repeat_uses', 'internal_opacity_comfort'],
    ['Winning recharge on paper while destroying every repeated cloud with Potent Extract.'],
    { itemId: 'vigo_s_fog_vial', addonIds: ['mushroom_formula', 'volcanic_stone'] }
  )
}

export function equipmentPlayerDoctrine (scenarioId) {
  return EQUIPMENT_PLAYER_DOCTRINE[scenarioId] || null
}
